import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import config from '../config';

const MIN_PASSWORD_LENGTH = 8;

export default function RegisterPage() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!authLoading && user) return <Navigate to={from} replace />;

  // The route is removed from the router when sign-ups are closed, so this is
  // only reachable by a stale bookmark. Say what happened rather than showing a
  // form the API is going to refuse.
  if (!config.registrationOpen) {
    return (
      <main className="auth-page">
        <Link to="/login" className="auth-brand">Language Lab</Link>
        <div className="form-card">
          <h1 className="form-title">Sign-ups are closed</h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            This is a private site and is not accepting new accounts.
          </p>
        </div>
        <p className="auth-alt muted">
          <Link to="/login">Back to sign in</Link>
        </p>
      </main>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setLoading(true);
    try {
      await register(email, password, displayName);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create your account');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-page">
      <Link to="/login" className="auth-brand">Language Lab</Link>
      <p className="auth-tagline">
        An account keeps your streak, your card setup and every word you have learned.
      </p>
      <div className="form-card">
        <h1 className="form-title">Create an account</h1>
        <form onSubmit={handleSubmit} className="form">
          <label className="field">
            Name
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" required />
          </label>
          <label className="field">
            Email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
          </label>
          <label className="field">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
            <span className="field-hint">At least {MIN_PASSWORD_LENGTH} characters.</span>
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
            {loading ? 'Creating…' : 'Create account'}
          </button>
        </form>
      </div>
      <p className="auth-alt muted">
        Already have an account? <Link to="/login" state={location.state}>Sign in</Link>
      </p>
    </main>
  );
}
